/* ==========================================================================
   Prime Organics Honey · PDP v2 · poh-reviews.js

   Reads the same public endpoint Judge.me's own widget reads and renders the
   real reviews in our markup. The embedded widget returns empty on this
   template (the judgeme metafield is blank here), so this is the reliable
   path to the 102 reviews that actually exist.

   Rules this file obeys, see SPEC.md §7.1 and §10 Q5:
   - it renders what the API returns and never invents, rewrites or trims a
     review body,
   - it never filters out low ratings; the real average, the real count and
     the real distribution are all displayed,
   - avatars are initial monograms, because no reviewer supplied a photo and
     inventing faces for real people is not an option.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-poh-reviews]');
  if (!root) return;

  var SHOP = root.getAttribute('data-shop');
  var PRODUCT = root.getAttribute('data-product');
  var PER = parseInt(root.getAttribute('data-per-page'), 10) || 8;
  if (!SHOP || !PRODUCT) return;

  var state = {
    page: 1, pages: 1,
    sort: root.getAttribute('data-sort') || 'most_recent',
    rating: 0, loading: false,
    all: null,     /* every review, fetched once, so sort and filter are local */
    featured: null /* uuids in the strip, so the list below can skip them */
  };

  var el = {
    summary: root.querySelector('[data-poh-rev-summary]'),
    avg: root.querySelector('[data-poh-rev-avg]'),
    stars: root.querySelector('[data-poh-rev-stars]'),
    count: root.querySelector('[data-poh-rev-count]'),
    bars: root.querySelector('[data-poh-rev-bars]'),
    toolbar: root.querySelector('[data-poh-rev-toolbar]'),
    filters: root.querySelector('[data-poh-rev-filters]'),
    sort: root.querySelector('[data-poh-rev-sort]'),
    list: root.querySelector('[data-poh-rev-list]'),
    featured: root.querySelector('[data-poh-rev-featured]'),
    featlist: root.querySelector('[data-poh-rev-featlist]'),
    more: root.querySelector('[data-poh-rev-more]'),
    src: root.querySelector('[data-poh-rev-src]'),
    empty: root.querySelector('[data-poh-rev-empty]')
  };

  /* The endpoint honours filter_rating but IGNORES sort entirely: every value
     comes back sort_key=created_at. A sort control driven by it would look
     like it worked and do nothing, so the full set is pulled once and sorted
     here instead. 102 reviews over 11 small pages, fetched in the background
     after the first paint. */
  function url(page, perPage, rating) {
    var q = 'https://judge.me/reviews/reviews_for_widget'
      + '?url=' + encodeURIComponent(SHOP)
      + '&shop_domain=' + encodeURIComponent(SHOP)
      + '&platform=shopify'
      + '&product_id=' + encodeURIComponent(PRODUCT)
      + '&per_page=' + perPage
      + '&page=' + page;
    if (rating) q += '&filter_rating=' + rating;
    return q;
  }

  function fetchPage(page, perPage, rating) {
    return fetch(url(page, perPage, rating), { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function fetchAll(firstPayload) {
    var per = 20;
    return fetchPage(1, per).then(function (d) {
      var total = (d.pagination && d.pagination.total_pages) || 1;
      var rest = [];
      for (var i = 2; i <= total; i++) rest.push(fetchPage(i, per));
      return Promise.all(rest).then(function (pages) {
        return pages.reduce(function (acc, x) { return acc.concat(x.reviews || []); }, (d.reviews || []).slice());
      });
    });
  }

  function sorted(list) {
    var out = list.slice();
    if (state.sort === 'highest_rating') out.sort(function (a, b) { return b.rating - a.rating || new Date(b.created_at) - new Date(a.created_at); });
    else if (state.sort === 'lowest_rating') out.sort(function (a, b) { return a.rating - b.rating || new Date(b.created_at) - new Date(a.created_at); });
    else out.sort(function (a, b) { return new Date(b.created_at) - new Date(a.created_at); });
    return out;
  }

  function visible() {
    var list = state.all.filter(function (r) { return !state.rating || +r.rating === state.rating; });
    /* whatever the featured strip is showing must not appear again below it */
    if (featuredShown() && state.featured) {
      list = list.filter(function (r) { return state.featured.indexOf(r.uuid) === -1; });
    }
    return sorted(list);
  }

  function featuredShown() {
    return !!(el.featured && !state.rating && state.sort !== 'lowest_rating');
  }

  /* Three real five-star reviews with the most to say. This orders, it never
     filters: the full list below still carries every rating, and the summary
     still shows the true average. */
  function renderFeatured() {
    if (!el.featured || !el.featlist || !state.all) return;
    var picks = state.all
      .filter(function (r) { return +r.rating === 5 && textOf(r.body_html).length > 90; })
      .sort(function (a, b) { return textOf(b.body_html).length - textOf(a.body_html).length; })
      .slice(0, 3);
    if (!picks.length) return;
    state.featured = picks.map(function (r) { return r.uuid; });
    el.featlist.innerHTML = picks.map(card).join('');
    el.featured.hidden = false;
  }

  /* render straight from the cached set */
  function renderLocal(reset) {
    if (reset) { state.page = 1; el.list.innerHTML = ''; }
    /* the featured strip is five-star by definition, so it has to step aside
       when a rating filter is on, or the filter looks broken */
    if (el.featured && state.all) el.featured.hidden = !featuredShown();
    var list = visible();
    var slice = list.slice(0, state.page * PER);
    el.list.innerHTML = slice.map(card).join('');
    el.more.hidden = slice.length >= list.length;
    if (el.empty) el.empty.hidden = list.length > 0;
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"]/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c];
    });
  }

  /* review bodies arrive as HTML from Judge.me. Keep the text, drop the tags,
     so a review can never inject markup into the page. */
  function textOf(html) {
    var d = document.createElement('div');
    d.innerHTML = html || '';
    return (d.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function starsHTML(n, cls) {
    var out = '';
    for (var i = 1; i <= 5; i++) {
      out += '<svg class="' + (cls || 'poh-star') + (i <= n ? '' : ' is-off') + '" viewBox="0 0 24 24" aria-hidden="true">'
           + '<path d="M12 2l3 6.6 7 .9-5.2 4.8 1.4 7-6.2-3.5L5.8 21l1.4-7L2 9.5l7-.9z"/></svg>';
    }
    return out;
  }

  function when(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d)) return '';
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  }

  /* Not one of these 102 reviews has a title, so the bold line on each card is
     the reviewer's OWN first sentence, set larger. Their words, unedited, just
     typographically led. Short reviews stay as a single line. */
  function split(body) {
    if (body.length <= 120) return { lead: body, rest: '' };
    var m = body.match(/^([\s\S]{0,130}?[.!?])\s+([\s\S]+)$/);
    if (m && m[2].length > 24) return { lead: m[1], rest: m[2] };
    return { lead: '', rest: body };
  }

  function card(r) {
    var name = (r.reviewer_name || '').trim() || 'Anonymous';
    var initial = (r.reviewer_initial || name.charAt(0) || '?').toUpperCase();
    var body = textOf(r.body_html);
    var badges = r.transparency_badges || [];
    var verified = r.verified_buyer === true || badges.indexOf('verified_buyer') > -1;
    var viaShop = badges.indexOf('review_written_in_shop_app') > -1 || r.source === 'shop-app';
    var place = (r.location || r.location_country || '').trim();

    var pics = (r.pictures_urls || []).map(function (p) {
      var src = p && (p.compact || p.original || p.small);
      return src ? '<img src="' + esc(src) + '" alt="" loading="lazy">' : '';
    }).join('');

    var parts = r.title ? { lead: textOf(r.title), rest: body } : split(body);

    return '<article class="poh-rev__card">'
      + '<div class="poh-rev__rating" aria-label="' + esc(r.rating) + ' out of 5">' + starsHTML(+r.rating) + '</div>'
      + (parts.lead ? '<h3 class="poh-rev__title">' + esc(parts.lead) + '</h3>' : '')
      + (parts.rest ? '<p class="poh-rev__body">' + esc(parts.rest) + '</p>' : '')
      + (pics ? '<div class="poh-rev__pics">' + pics + '</div>' : '')
      + (r.reply_content
          ? '<div class="poh-rev__reply"><b>' + esc(r.shop_reply_name || 'Prime Honey') + '</b>'
            + '<p>' + esc(textOf(r.reply_content)) + '</p></div>'
          : '')
      + '<footer class="poh-rev__who">'
        + '<span class="poh-rev__avatar" aria-hidden="true">' + esc(initial) + '</span>'
        + '<span class="poh-rev__meta">'
          + '<b>' + esc(name) + '</b>'
          + '<span class="poh-rev__sub">' + (verified ? 'Verified' : '') + (place ? ' &middot; ' + esc(place) : '')
            + (viaShop ? ' &middot; Shop app' : '') + '</span>'
        + '</span>'
        + (verified ? '<span class="poh-rev__pill">Verified</span>' : '<time class="poh-rev__date">' + esc(when(r.created_at)) + '</time>')
      + '</footer>'
      + '</article>';
  }

  function renderSummary(d) {
    /* the summary panel is optional now, so nothing here may assume it exists */
    if (el.summary && el.avg && el.stars && el.count && el.bars) {
      var avg = parseFloat(d.average_rating) || 0;
      el.avg.textContent = avg.toFixed(1);
      el.stars.innerHTML = starsHTML(Math.round(avg));
      el.count.textContent = d.number_of_reviews + (d.number_of_reviews === 1 ? ' review' : ' reviews');
      renderBars(d);
      el.summary.hidden = false;
    }
    renderChips(d);
    el.toolbar.hidden = false;
    if (el.src) el.src.hidden = false;   // source line is optional; the element is absent when the setting is blank
  }

  function renderBars(d) {
    var total = d.number_of_reviews || 1;
    el.bars.innerHTML = (d.histogram || []).map(function (h) {
      var pct = Math.round((h.frequency / total) * 100);
      return '<button type="button" class="poh-rev__bar" data-rating="' + h.rating + '"'
        + ' aria-label="' + h.frequency + ' reviews at ' + h.rating + ' stars">'
        + '<span class="poh-rev__barn">' + h.rating + '</span>'
        + '<span class="poh-rev__bartrack"><span style="width:' + pct + '%"></span></span>'
        + '<span class="poh-rev__barc">' + h.frequency + '</span>'
        + '</button>';
    }).join('');
  }

  function renderChips(d) {
    if (!el.filters) return;   /* chips are optional; sort still reaches every rating */
    el.filters.innerHTML = '<button type="button" class="poh-rev__chip is-on" data-rating="0">All</button>'
      + (d.histogram || []).filter(function (h) { return h.frequency > 0; })
        .map(function (h) {
          return '<button type="button" class="poh-rev__chip" data-rating="' + h.rating + '">' + h.rating + ' star</button>';
        }).join('');
  }

  function markFilters() {
    root.querySelectorAll('.poh-rev__chip').forEach(function (c) {
      c.classList.toggle('is-on', +c.getAttribute('data-rating') === state.rating);
    });
    root.querySelectorAll('.poh-rev__bar').forEach(function (b) {
      b.classList.toggle('is-on', +b.getAttribute('data-rating') === state.rating);
    });
  }

  function load(reset) {
    if (state.all) { renderLocal(reset); return; }
    if (state.loading) return;
    state.loading = true;
    el.more.disabled = true;

    /* first paint from page one, then the rest in the background */
    fetchPage(1, PER)
      .then(function (d) {
        renderSummary(d);
        var revs = d.reviews || [];
        el.list.innerHTML = revs.map(card).join('');
        if (el.empty) el.empty.hidden = revs.length > 0;
        el.more.hidden = !((d.pagination && d.pagination.total_pages) > 1);
        el.more.disabled = false;
        state.loading = false;
        return fetchAll(d);
      })
      .then(function (all) {
        if (!all || !all.length) return;
        state.all = all;
        renderFeatured();
        renderLocal(true);
      })
      .catch(function () {
        state.loading = false;
        el.more.disabled = false;
        /* the honest fallback: say nothing rather than show something invented */
        if (!el.list.children.length && el.empty) el.empty.hidden = false;
        /* a sort control that cannot sort is worse than no sort control */
        if (!state.all && el.sort) el.sort.parentNode.style.display = 'none';
      });
  }

  root.addEventListener('click', function (e) {
    var more = e.target.closest('[data-poh-rev-more]');
    if (more) { state.page++; state.all ? renderLocal(false) : load(false); return; }

    var f = e.target.closest('.poh-rev__chip, .poh-rev__bar');
    if (f) {
      var r = +f.getAttribute('data-rating');
      state.rating = (state.rating === r && r !== 0) ? 0 : r;
      markFilters();
      load(true);
    }
  });

  if (el.sort) {
    el.sort.value = state.sort;
    el.sort.addEventListener('change', function () {
      state.sort = el.sort.value;
      load(true);
    });
  }

  /* only fetch when the section is actually approached */
  if ('IntersectionObserver' in window) {
    var io = new IntersectionObserver(function (entries) {
      if (entries.some(function (x) { return x.isIntersecting; })) { io.disconnect(); load(true); }
    }, { rootMargin: '400px' });
    io.observe(root);
  } else {
    load(true);
  }
})();
